import { create } from 'zustand'
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware'

// Admin and non-admin (parent/teacher) sessions must not share one browser
// storage slot, or logging into one tab silently logs out the other tab.
// Writes are keyed by the role actually being persisted (read from the
// serialized value itself), not by the current pathname — pathname-based
// keying broke when a redirect fired before the URL changed, letting an
// admin session land in the non-admin slot (or vice versa). Reads still
// have to fall back to pathname since, before login, there's no role to
// key off of yet.
const AUTH_STORAGE_KEY = 'auth-storage'
const ADMIN_AUTH_STORAGE_KEY = 'admin-auth-storage'

function resolveReadStorageKey(): string {
  if (typeof window === 'undefined') return AUTH_STORAGE_KEY
  return window.location.pathname.startsWith('/admin')
    ? ADMIN_AUTH_STORAGE_KEY
    : AUTH_STORAGE_KEY
}

function resolveWriteStorageKey(value: string): string {
  try {
    const role = JSON.parse(value)?.state?.user?.role
    if (role === 'ADMIN') return ADMIN_AUTH_STORAGE_KEY
    if (role === 'PARENT' || role === 'TEACHER') return AUTH_STORAGE_KEY
  } catch {
    // fall through to pathname-based guess below
  }
  return resolveReadStorageKey()
}

const namespacedStorage: StateStorage = {
  getItem: (_name) => {
    if (typeof window === 'undefined') return null
    const preferred = resolveReadStorageKey()
    const fallback =
      preferred === ADMIN_AUTH_STORAGE_KEY ? AUTH_STORAGE_KEY : ADMIN_AUTH_STORAGE_KEY
    // 로그인 전에는 pathname으로 역할을 알 수 없어, 선호 슬롯이 비어 있으면 다른 슬롯도 본다.
    // (관리자 세션은 admin 슬롯에 있는데 PWA가 `/`로 부팅하면 선호가 auth-storage라
    //  admin 세션을 못 읽어 로그아웃으로 오판 → 홈으로 튕기던 문제를 막는다.)
    return localStorage.getItem(preferred) ?? localStorage.getItem(fallback)
  },
  setItem: (_name, value) => localStorage.setItem(resolveWriteStorageKey(value), value),
  removeItem: (_name) => localStorage.removeItem(resolveReadStorageKey()),
}

export type UserRole = 'PARENT' | 'TEACHER' | 'ADMIN'

export interface User {
  id: string
  email: string
  loginId?: string | null
  name: string
  role: UserRole
  profileImage?: string
  phone?: string | null
  hasAgreedTerms?: boolean
  teacher?: { verificationStatus: string; hasAgreedToLatestConsent?: boolean } | null
}

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  /** True after persist rehydration (so we don't redirect to login before store is restored) */
  _hasRehydrated: boolean
}

interface AuthActions {
  setUser: (user: User | null) => void
  setAccessToken: (token: string | null) => void
  setTokens: (accessToken: string, refreshToken: string) => void
  login: (user: User, accessToken: string, refreshToken?: string) => void
  logout: () => void
  setLoading: (loading: boolean) => void
  setRehydrated: () => void
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,
      _hasRehydrated: false,

      // Actions
      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
        }),

      setAccessToken: (accessToken) => set({ accessToken }),

      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),

      login: (user, accessToken, refreshToken) =>
        set({
          user,
          accessToken,
          refreshToken: refreshToken || null,
          isAuthenticated: true,
          isLoading: false,
        }),

      logout: () => {
        const role = get().user?.role
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        })
        // Clear persisted auth immediately so the next rehydration (e.g. when
        // another user opens the app) does not restore the previous user's data.
        // Key off the role that was actually logged in (not pathname) so this
        // can't clear the wrong tab's slot.
        if (typeof window !== 'undefined') {
          try {
            const key = role === 'ADMIN' ? ADMIN_AUTH_STORAGE_KEY : AUTH_STORAGE_KEY
            localStorage.removeItem(role ? key : resolveReadStorageKey())
          } catch {
            // ignore
          }
        }
      },

      setLoading: (isLoading) => set({ isLoading }),
      setRehydrated: () => set({ _hasRehydrated: true }),
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: createJSONStorage(() => namespacedStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      // Prevent automatic localStorage read before React hydration.
      // StoreHydration component calls rehydrate() after mount.
      skipHydration: true,
      onRehydrateStorage: () => (state, err) => {
        if (!err) useAuthStore.getState().setRehydrated()
      },
    }
  )
)
